import { Capacitor } from '@capacitor/core';
import { Haptics, NotificationType } from '@capacitor/haptics';
import * as Sentry from '@sentry/vue';
import { AfterFetchContext, BeforeFetchContext, createFetch, UseFetchReturn } from '@vueuse/core';
import { h } from 'vue';

import SubscribeDialog from '../../../app/components/SubscribeDialog.vue';
import { NETWORK_OFFLINE_ALERT_KEY, alerts, createAlert } from '../../alerts.ts';
import { router } from '../../app.ts';
import AuthConfirmationDialog from '../../auth/components/AuthConfirmationDialog.vue';
import { applyRequestHeaders, responseErrorMessage } from '../../http.ts';
import { modal } from '../../modal.ts';
import { models } from '../../models.ts';
import { payload } from '../../payload.ts';
import { useGlobalStore } from '../../stores/global.ts';
import { syncServerTime } from '../serverTime.ts';
import { markReceived, markStarted } from './writes.ts';

export const JSON_API_MEDIA_TYPE = 'application/vnd.api+json';

function beforeFetch(ctx: BeforeFetchContext) {
    ctx.options.headers = applyRequestHeaders(new Headers(ctx.options.headers));
    markStarted(ctx);

    return ctx;
}

export class FetchError extends Error {
    public preventAlert: () => void;
    public dismissAlert: () => void;

    constructor(
        public response: Response,
        public data: any,
    ) {
        let message = responseErrorMessage(response, data);

        if (!message) {
            switch (response?.status) {
                case 422: // Unprocessable Entity
                    message = 'The given data was invalid.';
                    break;

                case 401: // Unauthorized
                case 403: // Forbidden
                    message = 'You do not have permission to perform this action.';
                    break;

                case 404: // Not Found
                case 410: // Gone
                    message = 'The requested resource was not found.';
                    break;

                case 503: // Service Unavailable
                    message = "We're doing some maintenance. Please try again in a few minutes.";
                    break;

                default:
                    message = response
                        ? `Something went wrong (${response.status})`
                        : `We're having trouble talking to eMedici. Please make sure you're online and try again.`;
            }
        }

        super(message);

        const alert = createAlert({ message, type: 'danger' });
        const key = `fetch-${response?.url}`;
        const alertTimeout = setTimeout(() => {
            alerts.show(alert, { key });

            if (Capacitor.getPlatform() === 'ios') {
                Haptics.notification({ type: NotificationType.Error }).catch(() => {});
            }
        });

        this.dismissAlert = () => alerts.dismiss(key);
        this.preventAlert = () => clearTimeout(alertTimeout);
    }
}

async function onFetchError(ctx: any) {
    let { response, data, error } = ctx;

    // Leave aborts as they are. useFetch reads this return value, and ignores
    // the error of a run a newer one has aborted (see patchedDependencies).
    if (error.name == 'AbortError') {
        return ctx;
    }

    try {
        data = JSON.parse(data);
    } catch {}

    if (data?.errors?.[0]?.code === 'trial_limit_reached') {
        const product = models
            .findAll('products')
            .find((product) => product.slug === router.currentRoute.value.params.product);

        if (product) {
            modal(() => h(SubscribeDialog, { product, message: data.errors[0].detail }));
        }

        return ctx;
    }

    error = new FetchError(response, data);

    if (response && [400, 405, 406, 409, 415].includes(response.status)) {
        // Every request error is captured from this same stack, so Sentry
        // would group unrelated failures together. Group by what the server
        // rejected instead, and include the request to show where it came from.
        Sentry.captureException(new Error(`Request ${response.status}: ${JSON.stringify(data)}`), {
            fingerprint: [
                'request',
                String(response.status),
                JSON.stringify(
                    data?.errors?.map?.(({ code, source }: { code?: string; source?: unknown }) => [
                        code,
                        source,
                    ]) ?? [],
                ),
            ],
            extra: { method: ctx.context?.options?.method, url: ctx.context?.url },
        });
    }

    return { ...ctx, error };
}

function afterFetch(ctx: AfterFetchContext) {
    markReceived(ctx.context, ctx.data);
    syncServerTime(ctx.response);
    alerts.dismiss(NETWORK_OFFLINE_ALERT_KEY);
    alerts.dismiss(`fetch-${ctx.response.url}`);
    return ctx;
}

export function handleReauthentication(
    request: UseFetchReturn<any>,
    onCancel?: () => void,
    intended?: () => string | undefined,
) {
    request.onFetchError(async () => {
        const status = request.statusCode.value;
        const actor = useGlobalStore().actor;
        if (!(status === 401 || status === 419 || status === 423) || !actor) return;

        request.error.value?.preventAlert?.();

        const confirmed = await new Promise<boolean>((resolve) => {
            modal(() =>
                h(AuthConfirmationDialog, {
                    start: {
                        screen: 'entry',
                        mode: 'reauthenticate',
                        email: actor.email,
                        methods: {
                            passkey: status === 423 && actor.hasPasskeys,
                            password: actor.hasPassword,
                        },
                        purpose: status === 423 ? 'confirm' : 'login',
                    },
                    intended: intended?.(),
                    onConfirmed: () => resolve(true),
                    onClose: () => resolve(false),
                }),
            );
        });

        if (confirmed) {
            void request.execute();
        } else {
            onCancel?.();
        }
    });
}

export const useRequestBase = createFetch({
    options: {
        beforeFetch,
        afterFetch,
        onFetchError,
    },
});

export const useRequest = ((...args) => {
    const request = useRequestBase(...(args as Parameters<typeof useRequestBase>));

    handleReauthentication(request);

    return request;
}) as typeof useRequestBase;

export const useApiRequestBase = createFetch({
    options: {
        beforeFetch(ctx) {
            ctx = beforeFetch(ctx);

            if (ctx.url.search(/https?:\/\//) !== 0) {
                ctx.url = `${payload.apiUrl || import.meta.env.VITE_API_URL}/${ctx.url}`;
            }

            ctx.options.headers = new Headers(ctx.options.headers);

            if (!ctx.options.headers.has('Accept')) {
                ctx.options.headers.set('Accept', JSON_API_MEDIA_TYPE);
            }

            return ctx;
        },
        afterFetch,
        onFetchError,
    },
});
